import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="文章未找到" description="抱歉，您查找的文章不存在" keywords="蘑菇视频app" canonicalUrl={`/article/${slug || ''}`} />
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen bg-background-50">
          <div className="container-page text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">文章未找到</h1>
            <p className="text-foreground-500 mb-8">抱歉，您查找的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '蘑菇视频',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main className="pt-24 pb-16 md:pb-20 min-h-screen bg-background-50">
        <article className="container-page max-w-4xl">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                {article.categoryName}
              </span>
              <span className="text-sm text-foreground-400">
                <i className="ri-calendar-line mr-1"></i>
                {article.publishDate}
              </span>
              <span className="text-sm text-foreground-400">
                <i className="ri-time-line mr-1"></i>
                阅读约{article.readingTime}分钟
              </span>
              <span className="text-sm text-foreground-400">
                <i className="ri-user-line mr-1"></i>
                {article.author}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500">{article.excerpt}</p>
          </div>

          <div className="w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed text-base"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-sm bg-secondary-100 text-secondary-800">
                  #{tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-5 font-heading">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-32 rounded-md overflow-hidden mb-3">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <span className="text-xs text-foreground-400">{ra.publishDate}</span>
                      <h4 className="text-sm font-semibold text-foreground-900 mt-1 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                        {ra.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}